import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '@/components/layout/PageContainer';
import { Toast } from '@/components/common/Toast';
import { AddressSearchModal } from '@/components/common/AddressSearchModal';
import { WeeklyOperatingHours, DayOperatingHours, createDefaultOperatingHours } from '@/components/common/WeeklyOperatingHours';
import { useSellerSignupData } from '@/hooks/useSellerSignupData';
import { STORE_TYPES } from '@/constants'; // STORE_TYPES import

export function SellerStoreInfoPage() {
  const navigate = useNavigate();
  const { storeInfo, updateStoreInfo } = useSellerSignupData();

  const [formData, setFormData] = useState({
    storeName: storeInfo.storeName || '',
    storePhone: storeInfo.storePhone || '',
    address: storeInfo.address || '',
    detailAddress: storeInfo.detailAddress || '',
    foodTypes: storeInfo.foodTypes || [] as string[],
    customFoodType: storeInfo.customFoodType || '',
    representativeMenus: storeInfo.representativeMenus || ['', '', '']
  });

  const [operatingHours, setOperatingHours] = useState<DayOperatingHours[]>(
    storeInfo.operatingHours && storeInfo.operatingHours.length > 0 
      ? storeInfo.operatingHours 
      : createDefaultOperatingHours()
  );
  const [showAddressModal, setShowAddressModal] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState<{ show: boolean; message: string }>({ show: false, message: '' });

  const handleBack = () => {
    navigate(-1);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // localStorage에 자동 저장
    updateStoreInfo({ [field]: value });
  };

  const handleMenuChange = (index: number, value: string) => {
    const newMenus = formData.representativeMenus.map((menu, i) => 
      i === index ? value : menu
    );
    setFormData(prev => ({
      ...prev,
      representativeMenus: newMenus
    }));
    
    // localStorage에 자동 저장
    updateStoreInfo({ representativeMenus: newMenus });
  };

  const handleFoodTypeChange = (type: string) => {
    setFormData(prev => {
      let newData;
      
      if (type === '기타') {
        // 기타 선택 시
        newData = {
          ...prev,
          foodTypes: ['기타'],
          customFoodType: prev.customFoodType
        };
      } else {
        // 다른 타입 선택 시 (항상 1개만 선택)
        newData = {
          ...prev,
          foodTypes: [type],
          customFoodType: ''  // 기타가 아니면 customFoodType 초기화
        };
      }
      
      // localStorage에 자동 저장
      updateStoreInfo({ 
        foodTypes: newData.foodTypes,
        customFoodType: newData.customFoodType
      });
      
      return newData;
    });
  };

  const handleAddressSelected = (addressData: {
    roadAddress: string;
    jibunAddress: string;
    zonecode: string;
  }) => {
    const newAddress = addressData.roadAddress || addressData.jibunAddress;
    setFormData(prev => ({
      ...prev,
      address: newAddress
    }));
    
    // localStorage에 자동 저장
    updateStoreInfo({ address: newAddress });
    
    // 주소 선택 후 모달 자동 닫기
    setShowAddressModal(false);
  };

  const isFormValid = () => {
    const hasFoodTypes = formData.foodTypes.length > 0 && 
      (formData.foodTypes.includes('기타') ? formData.customFoodType.trim() !== '' : true);
    
    const hasValidOperatingHours = operatingHours.some(day => 
      !day.isClosed && day.openTime && day.closeTime
    );
    
    return formData.storeName &&
           formData.storePhone &&
           formData.address &&
           hasFoodTypes &&
           hasValidOperatingHours &&
           formData.representativeMenus.some(menu => menu.trim() !== '');
  };

  const handleComplete = async () => {
    if (!isFormValid()) {
      setToast({ show: true, message: '모든 필수 항목을 입력해주세요' });
      return;
    }

    setIsLoading(true);
    
    try {
      // localStorage를 사용하므로 location.state 대신 직접 이동
      // 완료 페이지에서 localStorage에서 모든 데이터를 가져감
      navigate('/signup/seller/complete');
    } catch (error) {
      setToast({ show: true, message: '회원가입 처리 중 오류가 발생했습니다' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <PageContainer>
        {/* 헤더 */}
        <div className="flex items-center justify-center relative mb-8">
          <button
            onClick={handleBack}
            className="absolute left-0 p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <img
              src="/icons/icon_back.svg"
              alt="뒤로가기"
              className="w-6 h-6"
            />
          </button>
          <h1 className="text-2xl font-bold text-[#222222]">회원가입</h1>
        </div>

        {/* 단계 표시 */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-[#51C854] rounded-full flex items-center justify-center">
              <span className="text-white font-medium">1</span>
            </div>
            <div className="w-16 h-0.5 bg-[#51C854] mx-2"></div>
            <div className="w-8 h-8 bg-[#51C854] rounded-full flex items-center justify-center">
              <span className="text-white font-medium">2</span>
            </div>
            <div className="w-16 h-0.5 bg-[#51C854] mx-2"></div>
            <div className="w-8 h-8 bg-[#51C854] rounded-full flex items-center justify-center">
              <span className="text-white font-medium">3</span>
            </div>
          </div>
        </div>

        {/* 입력 폼 */}
        <div className="max-w-[300px] mx-auto space-y-6">
          {/* 매장명 */}
          <div>
            <label className="block text-base font-medium text-[#222222] mb-1">
              매장명
            </label>
            <input
              type="text"
              value={formData.storeName}
              onChange={(e) => handleInputChange('storeName', e.target.value)}
              placeholder="매장명을 입력해주세요"
              className="w-full h-[30px] px-3 text-base bg-[#F5F5F5] border-[1.5px] border-[#51C854] rounded-lg focus:outline-none placeholder:text-[#606060]"
            />
          </div>

          {/* 매장 전화번호 */}
          <div>
            <label className="block text-base font-medium text-[#222222] mb-1">
              매장 전화번호
            </label>
            <input
              type="tel"
              value={formData.storePhone}
              onChange={(e) => handleInputChange('storePhone', e.target.value)}
              placeholder="매장 전화번호를 입력해주세요"
              className="w-full h-[30px] px-3 text-base bg-[#F5F5F5] border-[1.5px] border-[#51C854] rounded-lg focus:outline-none placeholder:text-[#606060]"
            />
          </div>

          {/* 매장 주소 */}
          <div>
            <label className="block text-base font-medium text-[#222222] mb-1">
              매장 주소
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={formData.address}
                readOnly
                placeholder="주소 찾기 버튼을 눌러주세요"
                className="flex-1 h-[30px] px-3 text-base bg-[#F5F5F5] border-[1.5px] border-[#51C854] rounded-lg focus:outline-none placeholder:text-[#606060]"
              />
              <button
                type="button"
                onClick={() => setShowAddressModal(true)}
                className="h-[30px] px-4 bg-[#51C854] text-white text-sm font-medium rounded-lg hover:bg-[#46B649] transition-colors whitespace-nowrap min-w-[60px]"
              >
                검색
              </button>
            </div>
          </div>

          {/* 상세 주소 */}
          <div>
            <label className="block text-base font-medium text-[#222222] mb-1">
              상세 주소
            </label>
            <input
              type="text"
              value={formData.detailAddress}
              onChange={(e) => handleInputChange('detailAddress', e.target.value)}
              placeholder="상세 주소를 입력해주세요"
              className="w-full h-[30px] px-3 text-base bg-[#F5F5F5] border-[1.5px] border-[#51C854] rounded-lg focus:outline-none placeholder:text-[#606060]"
            />
          </div>

          {/* 음식 종류 (단일 선택) */}
          <div>
            <label className="block text-base font-medium text-[#222222] mb-2">
              음식 종류
            </label>
            <div className="grid grid-cols-3 gap-2">
              {STORE_TYPES.map((type) => (
                <button
                  key={type.id}
                  onClick={() => handleFoodTypeChange(type.label)}
                  className={`h-[30px] text-sm rounded-lg border transition-colors ${
                    formData.foodTypes.includes(type.label)
                      ? 'bg-[#51C854] text-white border-[#51C854]'
                      : 'bg-[#F5F5F5] text-[#222222] border-[#DEDEDE] hover:bg-[#EEEEEE]'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
            
            {formData.foodTypes.includes('기타') && (
              <input
                type="text"
                value={formData.customFoodType}
                onChange={(e) => handleInputChange('customFoodType', e.target.value)}
                placeholder="음식 종류를 직접 입력해주세요"
                className="w-full h-[30px] px-3 text-base bg-[#F5F5F5] border-[1.5px] border-[#51C854] rounded-lg focus:outline-none placeholder:text-[#606060] mt-2"
              />
            )}
            
            {/* 선택된 타입 표시 */}
            {formData.foodTypes.length > 0 && (
              <div className="mt-2">
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#51C854] text-white text-xs rounded-md">
                  {formData.foodTypes[0] === '기타' ? formData.customFoodType || '기타' : formData.foodTypes[0]}
                </span>
              </div>
            )}
          </div>

          {/* 운영 시간 */}
          <WeeklyOperatingHours
            operatingHours={operatingHours}
            onChange={(hours) => {
              setOperatingHours(hours);
              updateStoreInfo({ operatingHours: hours });
            }}
          />

          {/* 대표 메뉴 */}
          <div>
            <label className="block text-base font-medium text-[#222222] mb-2">
              대표 메뉴 (최대 3개)
            </label>
            <div className="space-y-2">
              {formData.representativeMenus.map((menu, index) => (
                <input
                  key={index}
                  type="text"
                  value={menu}
                  onChange={(e) => handleMenuChange(index, e.target.value)}
                  placeholder={`대표 메뉴 ${index + 1}`}
                  className="w-full h-[30px] px-3 text-base bg-[#F5F5F5] border-[1.5px] border-[#51C854] rounded-lg focus:outline-none placeholder:text-[#606060]"
                />
              ))}
            </div>
          </div>
        </div>

        {/* 회원가입 완료 버튼 */}
        <div className="max-w-[360px] mx-auto mt-12">
          <button
            onClick={handleComplete}
            disabled={!isFormValid() || isLoading}
            className={`w-full h-[31px] font-semibold text-xl rounded-xl transition-colors ${
              isFormValid() && !isLoading
                ? 'bg-[#51C854] text-white hover:bg-[#46B649]' 
                : 'bg-[#DEDEDE] text-[#999999] cursor-not-allowed'
            }`}
          >
            {isLoading ? '처리 중...' : '회원가입 완료'}
          </button>
        </div>
      </PageContainer>

      {/* 주소 검색 모달 */}
      <AddressSearchModal
        isOpen={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        onAddressSelected={handleAddressSelected}
      />

      {/* 토스트 메시지 */}
      {toast.show && (
        <Toast
          message={toast.message}
          isVisible={toast.show}
          onClose={() => setToast({ show: false, message: '' })}
        />
      )}
    </div>
  );
}